/**
 * ROLE-OBSERVER-OPTIONS.MODAL
 * Features hidden from the members of a role, in addition to those hidden by the account
 */
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { useAppSelector } from "@/core/root-reducer"
import Modal from "@/components/modal"
import Link from "@/components/link"
import Button from "@/components/button"
import ObserverFeaturesWidget from "@/widgets/observer-features.widget"
import { ObserverOptions } from "@/redux/account.types"
import { Role } from "@/redux/role.types"

interface Props {
  role: Role
  onClose: () => void
  onSave: (observerOptions: Partial<ObserverOptions>) => Promise<unknown> //Truthy when saved
}

export default function RoleObserverOptionsModal({ role, onClose, onSave }: Props) {
  const { t } = useTranslation()
  const accountOptions = useAppSelector((state) => state._session.accountOptions)
  const [options, setOptions] = useState<Partial<ObserverOptions>>(role.observerOptions)
  const [isSaving, setIsSaving] = useState(false)

  //Closes once saved: on error the modal stays open with the choices
  async function save(observerOptions: Partial<ObserverOptions>) {
    if (isSaving) return
    setIsSaving(true)
    const isSaved = await onSave(observerOptions)
    setIsSaving(false)
    if (isSaved) {
      toast(t("utils_saved"), { autoClose: 2000 })
      onClose()
    }
  }

  return (
    <Modal
      title={t("roles_observer_options_title", { name: role.name })}
      onClose={onClose}
      isLarge
      footer={
        <div className="flex items-center">
          {role.hasObserverOptions && (
            <Link isWithoutMargin className="!mr-[16px]" onClick={() => save({})}>
              {t("roles_observer_options_reset")}
            </Link>
          )}
          <Button onClick={onClose}>{t("utils_close")}</Button>
          <Button className="primary" isLoading={isSaving} onClick={() => save(options)}>
            {t("utils_next")}
          </Button>
        </div>
      }>
      {/** Annule le padding de la ListItem : les cases s'alignent sur le titre.
          Fonctionnalités à gauche ; filtres masqués, filtre de référence, comparaison et plans d'action à droite,
          à leur hauteur (la dernière ligne prend le reste de la colonne de gauche). */}
      <div className="mx-[-32px] md:grid md:grid-cols-2 md:grid-rows-[auto_auto_auto_1fr] items-start md:[&>*:first-child]:row-span-4">
        <ObserverFeaturesWidget
          options={options}
          locked={accountOptions}
          isRole
          onChange={(changes) => setOptions({ ...options, ...changes })}
        />
      </div>
    </Modal>
  )
}
